import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { EcmrExternalService } from '@app/core/services/ecmr-services/ecmr-external.service';
import { lastValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { EcmrService } from '@app/core/services/ecmr-services/ecmr.service';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/shared/material/material.module';
import { DynamicDisableControlDirective } from '@app/shared/components/ecmr/dynamic-disable-control.directive';

enum ImportMode {
    OLF_ECMR = 'OLF_ECMR',
    PDF = 'PDF',
}

@Component({
    selector: 'app-ecmr-import-dialog',
    templateUrl: './ecmr-import-dialog.component.html',
    styleUrl: './ecmr-import-dialog.component.scss',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
        TranslateModule,
        DynamicDisableControlDirective,
    ],
})
export class EcmrImportDialogComponent {
    currentImportMode: ImportMode = ImportMode.OLF_ECMR;

    fullLink: string = '';
    urlForm: FormGroup;

    public files: File[] = [];

    constructor(
        private dialogRef: MatDialogRef<EcmrImportDialogComponent>,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private ecmrExternalService: EcmrExternalService,
        private ecmrService: EcmrService,
        private sessionService: SessionService,
        private fb: FormBuilder
    ) {
        this.urlForm = new FormGroup({
            url: new FormControl('', [Validators.required]),
            ecmrId: new FormControl('', [Validators.required]),
            shareToken: new FormControl('', [Validators.required]),
        });
    }

    switchImportMode(mode: ImportMode): void {
        if (this.currentImportMode === mode) {
            return;
        }
        this.currentImportMode = mode;
    }

    isConfirmButtonDisabled(): boolean {
        if (this.currentImportMode === ImportMode.OLF_ECMR) {
            if (this.fullLink !== '') {
                return false;
            } else if (this.urlForm.valid) {
                return false;
            }
            return true;
        } else {
            return !(this.files.length > 0);
        }
    }

    async confirm() {
        if (this.currentImportMode === ImportMode.OLF_ECMR) {
            if (this.fullLink !== '') {
                try {
                    this.fullLink = this.fullLink.split(' ').join('');
                    await lastValueFrom(
                        this.ecmrExternalService.importEcmrWithFullLink({
                            fullLink: this.fullLink,
                            organizationSite:
                                this.sessionService.currentOrganizationSiteKey(),
                        })
                    );

                    this.dialogRef.close(true);

                    this.toastrService.success(
                        this.translateService.instant(
                            'ECMR.import_dialog.import_olf_success'
                        )
                    );
                } catch (error) {
                    this.toastrService.error(
                        this.translateService.instant(
                            'ECMR.import_dialog.import_olf_error'
                        )
                    );
                }
            } else if (this.urlForm.valid) {
                const url = this.urlForm.get('url')?.value;
                const ecmrId = this.urlForm.get('ecmrId')?.value;
                const shareToken = this.urlForm.get('shareToken')?.value;

                try {
                    await lastValueFrom(
                        this.ecmrExternalService.importEcmr({
                            url: url,
                            ecmrId: ecmrId,
                            shareToken: shareToken,
                            organizationSite:
                                this.sessionService.currentOrganizationSiteKey(),
                        })
                    );

                    this.dialogRef.close(true);

                    this.toastrService.success(
                        this.translateService.instant(
                            'ECMR.import_dialog.import_olf_success'
                        )
                    );
                } catch (error) {
                    this.toastrService.error(
                        this.translateService.instant(
                            'ECMR.import_dialog.import_olf_error'
                        )
                    );
                }
            }
        } else if (this.currentImportMode === ImportMode.PDF) {
            if (this.files.length > 0) {
                try {
                    const siteId =
                        this.sessionService.currentOrganizationSiteKey();
                    for (let i = 0; i < this.files.length; i++) {
                        const file = this.files[i];

                        await lastValueFrom(
                            this.ecmrService.importPdfCmr(file, siteId)
                        );
                    }

                    this.dialogRef.close(true);

                    this.toastrService.success(
                        this.translateService.instant(
                            'ECMR.import_dialog.import_pdf_success'
                        )
                    );
                } catch (error) {
                    this.toastrService.error(
                        this.translateService.instant(
                            'ECMR.import_dialog.import_pdf_error'
                        )
                    );
                }
            }
        }
    }

    onFileDrop(event: DragEvent): void {
        event.preventDefault();
        const files = event.dataTransfer?.files;
        if (files) {
            this.processFiles(files);
        }
    }

    selectedFiles(event: any) {
        const files = event.target.files;
        this.processFiles(files);
    }

    processFiles(files: FileList): void {
        for (let i = 0; i < files.length; i++) {
            this.files.push(files[i]);
        }
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
    }

    removeFile(index: number): void {
        this.files.splice(index, 1);
    }

    protected readonly ImportMode = ImportMode;
}
